import { ITradeinContext } from '@/models/ITradein'
import { IAppStore } from '@/models/store.model'
import { webApiService } from '@/services'
import React, { createContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

export type TradeinContextType = {
  ctx: ITradeinContext;
  saveCtx: (ctx: ITradeinContext) => void;
};

const EmptyObject: ITradeinContext = {
  tradein: {
    id: 0,
    id_sbu: 0,
    sbu: '',
    brand: '',
    model: '',
    phone: '',
    cpu: '',
    cancel_reason: '',
    cpu_generation: '',
    screen: '',
    ram: '',
    capacity: '',
    folio_coppel: '',
    name: '',
    lastname: '',
    email: '',
    contract_email: '',
    rut: '',
    discount_amount: 0,
    serie: '',
    login_code: '',
    certificate_theft: '',
    certificate_possession: '',
    entry_form_id: 0,
    entry_form_status: 0,
    docusign_id: '',
    id_status: 0,
    status: '',
    blacklistStatus: '',
    make: '',
    modelName: '',
    modelNo: '',
    memory: '',
    store: null,
    recalculate: '',
    wasRecalculated: 0,
    hasFiles: 0,
    new_discount_amount: 0,
    cpuGeneration: '',
    screenSize: '',
    storage: '',
    id_seller: 0,
    isBatteryHealthy: null,
    extraControlsQuoted: 0,
    extraControlsValue: '',
    extraControlsReceived: 0,
    isExtraControl: null,
    haveCharger: null,
    url_contract: '',
    url_contract_multiple: null,
    created_at: '',
    updated_at: '',
    batteryHealthPercentage: null,
    grade: null,
    grade_IA: null,
    grade_quality: null,
    recipient_name: '',
    recipient_lastname: '',
    recipient_rut: '',
    recipient_id: '',
    batteryDiscount: 0,
    osiptel_evaluation: null,
    document_cupon_used: null,
    device_purchased: null,
    seller_store_number: null,
    id_contribution: null,
    contribution_amount: null,
    isFeasible: 0,
    samsung_name: '',
    giftcard_email: null,
    ripley_card_number: null,
    device_plan: null,
    device_boost: null,
    ticket_url: null
  },
  formAnswers: [],
  statusHistory: [],
  questions: [],
  answers: [],
  entrys: [],
  certificateTheft: '',
  certificatePossession: '',
  actionType: '',
  certificateType: '',
  openModal: false,
  batteryLife: '',
  emailRecipient: '',
  rut: '',
  address: '',
  state: '',
  city: '',
  civil_state: '',
  bank: '',
  bank_account: '',
  interbank_account: '',
  bank_account_type: '',
  serie: '',
  files: [],
  reload: false,
  loading: false,
  displayContract: 0,
  phone: '',
  id_sbu: 0
}

export const TradeinContext = createContext<TradeinContextType | null>(null)

// idProps permite montar el provider fuera de la ruta del detalle (por ejemplo en un modal del listado),
// donde el id del tradein no viene en la url.
// light carga solo el tradein y su configuración, para los casos donde se monta un provider por fila
// del listado y no se necesitan el checklist, los ingresos ni las respuestas del formulario.
export const TradeinProvider: React.FC<{ children: React.ReactNode, idProps?: number | string, light?: boolean }> = ({ children, idProps, light }) => {
  const [ctx, setCtx] = useState<ITradeinContext>(EmptyObject)
  const userState = useSelector((store: IAppStore) => store.user)
  const { id: idParam } = useParams()
  const id = idProps ?? idParam
  const saveCtx = (_ctx: ITradeinContext) => {
    setCtx(_ctx)
  }

  const loadDataLight = (): void => {
    setCtx({ ...ctx, loading: true })
    Promise.all([
      webApiService.getById({ id_tradein: String(id), email: userState.email, id_role: userState.id_role }),
      webApiService.getTradeinConfig().then((res) => res.data[0])
    ]).then((res) => {
      setCtx({ ...ctx, tradein: res[0].data[0], loading: false, reload: false, displayContract: res[1].display_contract })
    }).catch((error) => {
      setCtx({ ...ctx, reload: false })
      console.log('Error loading data:', error)
    })
  }

  const loadData = (): void => {
    if (light) {
      loadDataLight()
      return
    }
    setCtx({ ...ctx, loading: true })
    Promise.all([
      webApiService.getById({ id_tradein: String(id), email: userState.email, id_role: userState.id_role }),
      webApiService.getChecklistQuestions(),
      webApiService.getEntrysById(String(id)),
      webApiService.getTradeinConfig().then((res) => res.data[0]),
      webApiService.getAllAnswers(String(id)).then((res) => res.data)
    ]).then((res) => {
      setCtx({ ...ctx, tradein: res[0].data[0], loading: false, questions: res[1].data, entrys: res[2].data, reload: false, displayContract: res[3].display_contract, formAnswers: res[4] })
    }).catch((error) => {
      setCtx({ ...ctx, reload: false })
      console.log('Error loading data:', error)
    })
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.reload])

  return <TradeinContext.Provider value={{ ctx, saveCtx }}>{children}</TradeinContext.Provider>
}
